import React, {useState} from "react";
import {observer} from "mobx-react";
import GeneralDialog from "./GeneralDialog";
import {useStores} from "../../Hooks";
import {MenuItem, Radio, TextField} from "@mui/material";
import Utility from "../../Utility";
import DateRangeIcon from '@mui/icons-material/DateRange';
import {payPerClickTypeEnum} from "../../serverClientShared/sharedEnums";
import dayjs from 'dayjs';
import {MobileDatePicker} from '@mui/x-date-pickers'

const array28 = [...Array(29).keys()].slice(1);

const PayPerClickSharingModal = observer(() => {
    const {modalStore, generalStore, procedureStore} = useStores();

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const payPerClick = modalStore.modalData.payPerClick || {}

    const handleRadioChange = e => modalStore.modalData.payPerClick.IsRecurring = e.target.value !== "isLimited"

    const handleChangeMaxSessions = e => {
        if (e.target.value > 10000) {
            modalStore.showErrorMessage(generalStore.translate('maxSessionsCannotExceed') + ' 10,000')
        } else modalStore.modalData.payPerClick.MaxSessions = Number(e.target.value);
    }

    const handleChangeSessionsRemainingTrigger = e => {
        if (modalStore.modalData.payPerClick.MaxSessions > 0 && e.target.value > modalStore.modalData.payPerClick.MaxSessions) {
            modalStore.showErrorMessage(generalStore.translate('notifyWhenLeftCannotExceedMaxSessions'))
        } else modalStore.modalData.payPerClick.SessionsRemainingTrigger = Number(e.target.value);
    }

    const handleChangeDayOfMonth = e => modalStore.modalData.payPerClick.RecurringDayOfMonth = e.target.value;

    function saveSelectedDate(date) {
        if (!!date && new Date(date).valueOf() < Date.now()) {
            modalStore.showErrorMessage(generalStore.translate('expirationDateMustBeAtLeastOneDayInTheFuture'));
        } else {
            payPerClick.ExpirationDate = date ? new Date(date) : null;
        }
    }

    const renderLimitedOption = () => {
        return (
            <div className="pay-per-click-option-container">
                <Radio
                    className="pay-per-click-radio"
                    checked={!payPerClick.IsRecurring}
                    onChange={handleRadioChange}
                    value="isLimited"
                    inputProps={{'aria-label': 'limited'}}
                />
                <div className="pay-per-click-option">
                    <div className="pay-per-click-option-title">
                        {generalStore.translate('limited')}
                    </div>
                    <div className={`pay-per-click-option-main${!!payPerClick.IsRecurring ? " disabled" : ""}`}>
                        <div className="sessions-inputs">
                            <div className="sessions-input-container">
                                <div className="sessions-input-title">
                                    {generalStore.translate('amountOfSessionsEnabled')}
                                </div>
                                <TextField
                                    variant="outlined"
                                    className="sessions-input"
                                    placeholder={generalStore.translate('max10000')}
                                    value={!payPerClick.IsRecurring ? String(payPerClick.MaxSessions || "") : ""}
                                    onChange={handleChangeMaxSessions}
                                    disabled={!!payPerClick.IsRecurring}
                                    inputProps={{'data-testid': 'publish-ppc-max-sessions'}}
                                />
                            </div>
                            <div className="sessions-input-container">
                                <div className="sessions-input-title">
                                    {generalStore.translate('notifyWhenLeft')}
                                </div>
                                <TextField
                                    variant="outlined"
                                    className="sessions-input"
                                    placeholder={generalStore.translate('insertNumber')}
                                    value={!payPerClick.IsRecurring ? String(payPerClick.SessionsRemainingTrigger || "") : ""}
                                    onChange={handleChangeSessionsRemainingTrigger}
                                    disabled={!!payPerClick.IsRecurring}
                                    inputProps={{'data-testid': 'publish-ppc-notify-when-left'}}
                                />
                            </div>
                        </div>
                        <div className="filler-text">
                            {generalStore.translate('andOr')}
                        </div>
                        <div className="sessions-input-container">
                            <div className="sessions-input-title">
                                {generalStore.translate('expirationDate')}
                            </div>
                            <div
                                className="pay-per-click-date-picker"
                                onClick={() => !payPerClick.IsRecurring && setIsPickerOpen(true)}
                            >
                                <div>
                                    {Utility.renderFullDate(payPerClick.ExpirationDate, generalStore.translate('dateLocale'))}
                                </div>
                                <DateRangeIcon style={!!payPerClick.IsRecurring ? {color: "#D9E0F0"} : null}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderRecurringOption = () => {
        return (
            <div className="pay-per-click-option-container">
                <Radio
                    className="pay-per-click-radio"
                    checked={!!payPerClick.IsRecurring}
                    onChange={handleRadioChange}
                    value="isRecurring"
                    inputProps={{'aria-label': 'recurring'}}
                    color="secondary"
                />
                <div className="pay-per-click-option">
                    <div className="pay-per-click-option-title">
                        {generalStore.translate('recurring')}
                    </div>
                    <div className={`pay-per-click-option-main${!payPerClick.IsRecurring ? " disabled" : ""}`}>
                        <div className="sessions-inputs">
                            <div className="sessions-input-container">
                                <div className="sessions-input-title">
                                    {generalStore.translate('amountOfSessionsEnabled')}
                                </div>
                                <TextField
                                    variant="outlined"
                                    className="sessions-input"
                                    placeholder={generalStore.translate('max10000')}
                                    value={!!payPerClick.IsRecurring ? String(payPerClick.MaxSessions || "") : ""}
                                    onChange={handleChangeMaxSessions}
                                    disabled={!payPerClick.IsRecurring}
                                />
                            </div>
                            <div className="sessions-input-container">
                                <div className="sessions-input-title">
                                    {generalStore.translate('notifyWhenLeft')}
                                </div>
                                <TextField
                                    variant="outlined"
                                    className="sessions-input"
                                    placeholder={generalStore.translate('insertNumber')}
                                    value={!!payPerClick.IsRecurring ? String(payPerClick.SessionsRemainingTrigger || "") : ""}
                                    onChange={handleChangeSessionsRemainingTrigger}
                                    disabled={!payPerClick.IsRecurring}
                                />
                            </div>
                        </div>
                        <div className="filler-text">
                            {generalStore.translate('and')}
                        </div>
                        <div className="sessions-input-container">
                            <div className="sessions-input-title">
                                {generalStore.translate('firstDayOfMonth')}
                            </div>
                            <TextField
                                className="pay-per-click-day-of-month-picker"
                                variant="outlined"
                                value={payPerClick.RecurringDayOfMonth || 1}
                                select
                                onChange={handleChangeDayOfMonth}
                                disabled={!payPerClick.IsRecurring}
                            >
                                {array28.map(day =>
                                    <MenuItem key={day} value={day}>
                                        {day}
                                    </MenuItem>
                                )}
                            </TextField>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <div className="procedure-project-title">
                    {payPerClick.ProcedureName} ({generalStore.translate('inProject')}: {payPerClick.ProjectName})
                </div>
                <div className="pay-per-click-options">
                    {renderLimitedOption()}
                    {renderRecurringOption()}
                </div>
                <MobileDatePicker
                    open={isPickerOpen}
                    sx={{display: 'none'}}
                    value={dayjs(payPerClick.ExpirationDate)}
                    onChange={saveSelectedDate}
                    onClose={() => setIsPickerOpen(false)}
                    onError={console.log}
                    disablePast
                    closeOnSelect
                />
            </div>
        )

    }

    const validatePayPerClick = () => {
        let isValid;
        if (!payPerClick.IsRecurring) {
            if (!!payPerClick.MaxSessions && modalStore.modalData.payPerClick.SessionsRemainingTrigger > modalStore.modalData.payPerClick.MaxSessions) {
                modalStore.showErrorMessage(generalStore.translate('notifyWhenLeftCannotExceedMaxSessions'));
                return false
            }
            isValid = !!payPerClick.ExpirationDate || !!payPerClick.MaxSessions;
            if (!isValid) {
                modalStore.showErrorMessage(generalStore.translate('pleaseEnterExpirationDateAndOrMaxSessions'));
            }
        } else {
            isValid = !!payPerClick.MaxSessions;
            if (!isValid) {
                modalStore.showErrorMessage(generalStore.translate('pleaseEnterMaxSessions'));
            }
            if (!payPerClick.RecurringDayOfMonth) {
                payPerClick.RecurringDayOfMonth = 1;
            }
        }
        return isValid
    }

    const updatePayPerClick = async () => {
        const isValid = validatePayPerClick();
        if (!isValid) {
            return
        }
        if (!payPerClick.IsRecurring) {
            payPerClick.SharingType = payPerClickTypeEnum.LIMITED;
            payPerClick.RecurringDayOfMonth = null;
        } else {
            payPerClick.SharingType = payPerClickTypeEnum.RECURRING;
            payPerClick.ExpirationDate = null;
        }
        if (payPerClick.isTemp) {
            modalStore.modalData.payPerClick = payPerClick;
            modalStore.modalData.afterSave();
            modalStore.isPayPerClickSharingOpen = false;
            return
        }
        await procedureStore.updatePayPerClick(payPerClick);
        modalStore.isPayPerClickSharingOpen = false;
        if (!!modalStore.modalData.afterSave) {
            await modalStore.modalData.afterSave(payPerClick);
        }
        Utility.sleep(100).then(() => modalStore.resetModalData());
    }

    return (
        <GeneralDialog
            openControl='isPayPerClickSharingOpen'
            className='procedure-option-dialog pay-per-click-dialog'
            data-testid="pay-per-click-dialog"
            title={generalStore.translate('payPerClickSharing')}
            body={renderBody()}
            isConfirmDisabled={false}
            onConfirm={updatePayPerClick}
            confirmText={generalStore.translate('allow')}
            specialOnClose={payPerClick.isTemp ? () => modalStore.isPayPerClickSharingOpen = false : null}
        />
    );

});

export default PayPerClickSharingModal;
